import type { AccountSearchResult, SearchAccountsResponse } from "@wallyo/contracts/console/accounts";
import { t } from "../../i18n";

/** The server's minimum (`searchAccountsSchema` in server/http/validation/console.ts). */
export const SEARCH_MIN_LENGTH = 2;

/** A problem with the query, or null when it can be sent. */
export function queryProblem(query: string): string | null {
  return query.trim().length >= SEARCH_MIN_LENGTH ? null : t("customers.search.tooShort");
}

/**
 * The search as the screen holds it: the pages read so far, one after the
 * other. A new query replaces everything; "Charger plus" appends the next
 * page. A failed "Charger plus" keeps what is already shown.
 */
export type SearchState = {
  status: "idle" | "loading" | "ready" | "error";
  results: readonly AccountSearchResult[];
  matchedBy: SearchAccountsResponse["matchedBy"] | null;
  normalisedPhone: string | null;
  nextCursor: string | null;
  error: unknown;
  loadingMore: boolean;
  moreError: unknown;
};

export type SearchEvent =
  | { type: "idle" }
  | { type: "search" }
  | { type: "loaded"; page: SearchAccountsResponse }
  | { type: "failed"; error: unknown }
  | { type: "loadMore" }
  | { type: "loadedMore"; page: SearchAccountsResponse }
  | { type: "moreFailed"; error: unknown };

export const INITIAL_SEARCH_STATE: SearchState = {
  status: "idle",
  results: [],
  matchedBy: null,
  normalisedPhone: null,
  nextCursor: null,
  error: null,
  loadingMore: false,
  moreError: null,
};

export function searchReducer(state: SearchState, event: SearchEvent): SearchState {
  switch (event.type) {
    case "idle":
      return INITIAL_SEARCH_STATE;
    case "search":
      return { ...INITIAL_SEARCH_STATE, status: "loading" };
    case "loaded":
      return {
        ...INITIAL_SEARCH_STATE,
        status: "ready",
        results: event.page.results,
        matchedBy: event.page.matchedBy,
        normalisedPhone: event.page.normalisedPhone,
        nextCursor: event.page.nextCursor,
      };
    case "failed":
      return { ...INITIAL_SEARCH_STATE, status: "error", error: event.error };
    case "loadMore":
      return { ...state, loadingMore: true, moreError: null };
    case "loadedMore": {
      // A page read twice (a retried click) adds nothing it already has.
      const seen = new Set(state.results.map((result) => result.userId));
      const fresh = event.page.results.filter((result) => !seen.has(result.userId));
      return {
        ...state,
        results: [...state.results, ...fresh],
        nextCursor: event.page.nextCursor,
        loadingMore: false,
        moreError: null,
      };
    }
    case "moreFailed":
      return { ...state, loadingMore: false, moreError: event.error };
  }
}
